import { useMemo } from "react";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useProviderProfileLight } from "@/queries/provider/setting/use-provider-profile-light";
import { useUserProfile } from "@/queries/use-user-profile";
import {
  getIdFromObj,
  getUserAlias,
  getValueFromObj,
  getXtenantId,
} from "@/utils/utils";
import { PROVIDER_GROUP_STORAGE_KEY } from "../nav-bar-constant";
import type { NavBarLocation, NavBarUser, NavBarView } from "../nav-bar-types";
import { getNavBarShortName } from "../nav-bar-utils";

interface UseNavBarSessionResult {
  user: NavBarUser;
  isProviderView: boolean;
  /** Provider-only session context; undefined in the admin portal. */
  providerGroup?: string;
  loggedLocation?: NavBarLocation;
}

/**
 * One display identity plus session context for the bar, whichever portal
 * rendered it: providers come from the provider profile, admins and staff from
 * the user profile.
 */
export const useNavBarSession = (view: NavBarView): UseNavBarSessionResult => {
  const [me] = useCurrentUser();

  const isProviderView = view === "provider";

  // Staff are routed into the provider portal, so `view` alone cannot tell
  // them apart from an actual provider.
  const isProviderUserLoggedIn = isProviderView && me?.roleType !== "STAFF";

  const { data: providerProfile } = useProviderProfileLight(
    getXtenantId() ?? undefined,
    isProviderUserLoggedIn
  );
  const { data: adminProfile } = useUserProfile({
    disabled: isProviderUserLoggedIn,
  });

  const user = useMemo<NavBarUser>(() => {
    const provider = providerProfile?.data;
    const admin = adminProfile?.data;
    const profile = provider ?? admin;
    const firstName: string = profile?.firstName ?? "";
    const lastName: string = profile?.lastName ?? "";

    return {
      fullName: [firstName, lastName].filter(Boolean).join(" "),
      shortName: getNavBarShortName(
        firstName,
        lastName,
        isProviderUserLoggedIn
      ),
      initials: getUserAlias(firstName, lastName),
      role: provider?.role ?? admin?.roleString ?? admin?.role ?? "",
      avatarUrl: profile?.avatar ?? me?.avatar ?? undefined,
    };
  }, [providerProfile, adminProfile, me?.avatar, isProviderUserLoggedIn]);

  const providerGroup = isProviderView
    ? (localStorage.getItem(PROVIDER_GROUP_STORAGE_KEY) ?? undefined)
    : undefined;

  const loggedLocation = useMemo<NavBarLocation | undefined>(() => {
    if (!isProviderView) return undefined;
    const id = getIdFromObj(me?.workLocations);
    if (!id) return undefined;
    return { id, label: getValueFromObj(me?.workLocations) ?? "" };
  }, [isProviderView, me?.workLocations]);

  return { user, isProviderView, providerGroup, loggedLocation };
};
